import { Box, ButtonBase, Icon, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSidebarVisual } from '../../contexts/SidebarVisualContext';
import React, { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Paragraph, Span } from '../Typography';
import MatxVerticalNavExpansionPanel from './MatxVerticalNavExpansionPanel';

const ListLabel = styled(Paragraph, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})(({ theme, expanded }) => ({
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  marginTop: '24px',
  marginLeft: '12px',
  marginBottom: '8px',
  textTransform: 'uppercase',
  display: expanded ? undefined : 'none',
  color: 'rgba(255,255,255,.64)',
  opacity: 0.85,
  '&:first-of-type': {
    marginTop: '8px',
  },
}));

const ExtAndIntCommon = {
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '8px',
  height: 38,
  whiteSpace: 'pre',
  marginBottom: '4px',
  textDecoration: 'none',
  justifyContent: 'space-between',
  transition:
    'background 180ms ease, color 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
  '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
  '&.compactNavItem': {
    overflow: 'hidden',
    justifyContent: 'center !important',
  },
  '& .icon': {
    fontSize: '20px',
    paddingLeft: '14px',
    paddingRight: '12px',
    verticalAlign: 'middle',
    opacity: 0.95,
  },
};
const ExternalLink = styled('a')(({ theme }) => ({
  ...ExtAndIntCommon,
  color: theme.palette.text.primary,
}));

const InternalLink = styled(Box)(({ theme }) => ({
  '& a': {
    ...ExtAndIntCommon,
    color: theme.palette.text.primary,
    border: '1px solid transparent',
    boxSizing: 'border-box',
  },
  '& a.navItemActive': {
    fontWeight: 600,
    background: '#0b63d9',
    borderColor: '#0b63d9',
    boxShadow: '0 8px 18px rgba(11, 99, 217, 0.22)',
    color: '#ffffff',
  },
  '& a.navItemActive:hover': {
    background: '#0a59c3',
    borderColor: '#0a59c3',
  },
  '& a.navItemActive .icon': {
    color: '#ffffff',
    opacity: 1,
  },
  '& a.navItemActive .nav-item-label': {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  '& a.navItemActive .nav-badge': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.25),
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
  },
}));

const StyledText = styled(Span, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})(({ expanded }) => ({
  fontSize: '12px',
  paddingLeft: '0.35rem',
  display: expanded ? undefined : 'none',
  fontWeight: 500,
}));

const BulletIcon = styled('div')(({ theme }) => ({
  padding: '2px',
  marginLeft: '22px',
  marginRight: '8px',
  overflow: 'hidden',
  borderRadius: '300px',
  background: theme.palette.text.primary,
  opacity: 0.9,
}));

const BadgeValue = styled('div')(({ theme }) => ({
  padding: '2px 8px',
  overflow: 'hidden',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  color: theme.palette.text.primary,
  border: '1px solid transparent',
  transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease',
}));

const MatxVerticalNav = ({ items }) => {
  const { isSidebarExpanded } = useSidebarVisual();
  const expanded = isSidebarExpanded;
  const compactNav = !expanded;

  const renderLevels = (data) => {
    return data.map((item, index) => {
      if (item.type === 'label')
        return (
          <ListLabel key={index} expanded={expanded} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel sidebarExpanded={expanded} item={item} key={index}>
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === 'extLink') {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${compactNav && 'compactNavItem'}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ButtonBase key={item.name} name="child" sx={{ width: '100%' }}>
              {(() => {
                if (item.icon) {
                  return <Icon className="icon">{item.icon}</Icon>;
                } else {
                  return <span className="item-icon icon-text">{item.iconText}</span>;
                }
              })()}
              <StyledText expanded={expanded} className="sidenavHoverShow nav-item-label">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue className="nav-badge">{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index}>
            <NavLink
              to={item.path}
              className={({ isActive }) => {
                return isActive
                  ? `navItemActive ${compactNav && 'compactNavItem'}`
                  : `${compactNav && 'compactNavItem'}`
              }}
            >
              <ButtonBase key={item.name} name="child" sx={{ width: '100%' }}>
                {item?.icon ? (
                  <Icon className="icon" sx={{ width: 36 }}>
                    {item.icon}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className={`nav-bullet`}
                      sx={{ display: compactNav && 'none' }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: '20px',
                        fontSize: '11px',
                        display: expanded && 'none',
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <StyledText expanded={expanded} className="sidenavHoverShow nav-item-label">
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow nav-badge">{item.badge.value}</BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return <div className="navigation">{renderLevels(items)}</div>;
};

export default React.memo(MatxVerticalNav);
